import { createSlice, createSelector } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from "../common/reducers/utilities";
import { ensureArray } from "../common/utils/arrayUtilities";
import { createEagerSelector, eagerMetaFactory } from "../common/reducers/pendingUpdateSlice";

const epcrMedicationSlice = createSlice({
    name: 'epcrMedications',
    initialState: {
        data: {},
        defs: []
    },
    reducers: {
        setDefs: (state, action) => {
            state.defs = action.payload;
        },
        addOrUpdate: (state, action) => {
            for (const med of ensureArray(action.payload)) {
                if (!state.data[med.pcrid]) {
                    state.data[med.pcrid] = [med];
                } else {
                    let idx = state.data[med.pcrid].findIndex(m => med.id === m.id);
                    if (idx >= 0) {
                        state.data[med.pcrid].splice(idx, 1, med);
                    } else {
                        state.data[med.pcrid] = [...state.data[med.pcrid], med];
                    }
                }
            }
        },
        remove: (state, action) => {
            for (const med of ensureArray(action.payload)) {
                let idx = state.data[med.pcrid].findIndex(m => m.id === med.id);
                if (idx >= 0) {
                    state.data[med.pcrid].splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => epcrMedicationSlice.getInitialState());
    }
});

// #region network operations
const eagerMeta = eagerMetaFactory(epcrMedicationSlice.name);
const fetchEpcrMedictions = pcrid => network => network.get('/api/v1/epcrmedications/' + encodeURIComponent(pcrid), epcrMedicationSlice.actions.addOrUpdate);
const fetchEpcrMedicationDefs = (network) => network.get('/api/v1/epcrmedicationdefs', epcrMedicationSlice.actions.setDefs);
export const addEpcrMedication = (network, data) => network.put('/api/v1/epcrmedication', data, epcrMedicationSlice.actions.addOrUpdate, eagerMeta(data.id, { fk: data.pcrid })); // TODO better failure
export const removeEpcrMedication = (network, data) => network.delete('/api/v1/epcrmedication/' + encodeURIComponent(data.id), epcrMedicationSlice.actions.remove, eagerMeta(data.id, { fk: data.pcrid })); // TODO better failure
// #endregion

// #region selectors
const selectEpcrMedicationDefs = state => state.epcrMedications.defs;
const selectEpcrMedications = state => state.epcrMedications.data;

const eagerSelector = createEagerSelector(epcrMedicationSlice.name, { mode: 'mapToArray' });
const selectEpcrMedicationByID = eagerSelector(createSelector([selectEpcrMedications, (medications, pcrid) => pcrid], (medications, pcrid) => medications[pcrid] ?? []));
const selectEpcrMedication = pcrid  => state => {
    return selectEpcrMedicationByID(state, pcrid);
}
// #endregion

// #region listeners
const listen = {
    epcrMedicationAdded: (med, { dispatch }) => dispatch(epcrMedicationSlice.actions.addOrUpdate(med)),
    epcrMedicationRemoved: (med, { dispatch }) => dispatch(epcrMedicationSlice.actions.remove(med))
}
// #endregion 

export const [useEpcrMedications, realizeEpcrMedications] = dataHookFactory(selectEpcrMedication, initMapFactory(fetchEpcrMedictions, listen, pcrid => pcrid?.toString()));
export const [useEpcrMedicationDefs, realizeEpcrMedicationDefs] = dataHookFactory(selectEpcrMedicationDefs, { run: fetchEpcrMedicationDefs });

const reducerDetails = { name: epcrMedicationSlice.name, reducer: epcrMedicationSlice.reducer };
export default reducerDetails;

